import React, { useState, useEffect, useMemo } from "react";
import { Modal, ModalHeader, ModalBody, Row, Col, Label } from "reactstrap";
import { Formik, Field, Form, ErrorMessage } from "formik";
import * as Yup from "yup";
import { toast } from "react-toastify";
import axiosInstance from "Axiosinstance";

const EMPTY_COMPANY = {
  company_uid: "",
  company_name: "",
  company_phone_country_code: "+91",
  company_phone: "",
  company_key_representative_email: "",
  no_of_branches: 1,
  rate_of_insurance: {
    "3000": "",
    "5000": "",
  },
  wallet_balance: "",
  purpose: [],
  company_address: "",
  company_state: "",
  company_gst_no: "",
};

const PURPOSE_OPTIONS = [
  { value: "bag_protection_plan", label: "Bag Protection Plan" },
  { value: "qr_bag_with_protection", label: "QR Bag with Protection" },
  { value: "etags", label: "Etags" },
];

// Fields that are optional on create, but once a value exists on the
// company (i.e. we're editing and the original had a non-empty value),
// they become required so the user can't clear them back to blank.
// These are only shown at all when company_login is true.
const LOCKABLE_FIELDS = ["company_address", "company_state", "company_gst_no"];

const FIELD_LABELS = {
  company_address: "Company address",
  company_state: "Company state",
  company_gst_no: "Company GST number",
};

// Builds the Yup schema based on whether we're editing, which of the
// lockable fields already had a value on the original company, and whether
// this is a "company login" company (which flips which fields are visible):
// - companyLogin === true  -> address/state/gst are shown & validated, purpose is hidden & skipped
// - companyLogin === false -> purpose is shown & validated, address/state/gst are hidden & skipped
const buildValidationSchema = (isEditing, editingCompany, companyLogin) => {
  const shape = {
    company_name: Yup.string()
      .trim()
      .max(30, "Company name must be 30 characters or fewer")
      .required("Company name is required"),
    company_key_representative_email: Yup.string()
      .trim()
      .email("Enter a valid email address")
      .required("Company email is required"),
    company_phone: Yup.string()
      .trim()
      .matches(/^\d{10}$/, "Enter a valid 10-digit phone number")
      .required("Company phone is required"),
    no_of_branches: Yup.number()
      .typeError("No. of branches must be a number")
      .integer("No. of branches must be a whole number")
      .min(1, "Must be at least 1")
      .max(99, "Must be at most 99")
      .required("No. of branches is required"),
  };

  // Insurance rates are not applicable to company-login companies.
  if (!companyLogin) {
    shape.rate_of_insurance = Yup.object()
      .shape({
        "3000": Yup.number()
          .typeError("Rate for ₹3,000 insurance must be a number")
          .integer("Rate for ₹3,000 insurance must be a whole number")
          .min(1, "Rate must be at least 1")
          .max(1000000, "Rate must be at most 1000000")
          .required("Rate for ₹3,000 insurance is required"),
        "5000": Yup.number()
          .typeError("Rate for ₹5,000 insurance must be a number")
          .integer("Rate for ₹5,000 insurance must be a whole number")
          .min(1, "Rate must be at least 1")
          .max(1000000, "Rate must be at most 1000000")
          .required("Rate for ₹5,000 insurance is required"),
      })
      .required("Insurance rates are required");

    // Wallet balance is set on create only; edit uses the dedicated recharge page.
    if (!isEditing) {
      shape.wallet_balance = Yup.number()
        .typeError("Wallet balance must be a number")
        .integer("Wallet balance must be a whole number")
        .min(0, "Wallet balance must be at least 0")
        .max(100000000, "Wallet balance is too large")
        .required("Wallet balance is required");
    }
  }

  // Purpose is only visible (and therefore only required) when this is NOT
  // a company_login company.
  shape.purpose = companyLogin
    ? Yup.array().of(Yup.string()).notRequired()
    : Yup.array()
        .of(Yup.string())
        .min(1, "Select at least one purpose")
        .required("Purpose is required");

  LOCKABLE_FIELDS.forEach((field) => {
    // Hidden entirely when company_login is false, so nothing to validate.
    if (!companyLogin) {
      shape[field] = Yup.string().trim().notRequired();
      return;
    }

    const hadValueOriginally =
      isEditing && !!editingCompany?.[field]?.toString().trim();

    shape[field] = hadValueOriginally
      ? Yup.string()
          .trim()
          .required(`${FIELD_LABELS[field]} cannot be cleared once set`)
      : Yup.string().trim().notRequired();
  });

  return Yup.object().shape(shape);
};

// Small red error text shown below a field, only after it's touched/submitted
const FieldError = ({ name }) => (
  <ErrorMessage name={name}>
    {(msg) => (
      <div style={{ color: "#dc3545", fontSize: "0.8rem", marginTop: "4px" }}>
        {msg}
      </div>
    )}
  </ErrorMessage>
);

// Reusable fixed +91 phone field. Pass the Formik field name (e.g.
// "company_phone").
const FixedCountryCodePhoneField = ({ name, errors, touched }) => (
  <div className="input-group">
    <span className="input-group-text">+91</span>
    <Field name={name}>
      {({ field, form }) => (
        <input
          {...field}
          type="tel"
          inputMode="numeric"
          maxLength="10"
          className={"form-control" + (errors && touched ? " is-invalid" : "")}
          onChange={(e) => {
            const digits = e.target.value.replace(/\D/g, "").slice(0, 10);
            form.setFieldValue(field.name, digits);
          }}
          onKeyDown={(e) => {
            const allowed = [
              "Backspace",
              "Delete",
              "Tab",
              "ArrowLeft",
              "ArrowRight",
              "Home",
              "End",
            ];
            if (
              allowed.includes(e.key) ||
              (e.ctrlKey && ["a", "c", "v", "x"].includes(e.key.toLowerCase()))
            )
              return;
            if (!/^\d$/.test(e.key)) e.preventDefault();
          }}
        />
      )}
    </Field>
  </div>
);

// Multi-select for purpose: Bag Protection Plan / QR Bag with Protection / Etags
const PurposeMultiSelectField = ({ name, options, errors, touched }) => (
  <Field name={name}>
    {({ field, form }) => (
      <select
        {...field}
        multiple
        className={"form-control" + (errors && touched ? " is-invalid" : "")}
        value={field.value || []}
        onChange={(e) => {
          const selected = Array.from(
            e.target.selectedOptions,
            (opt) => opt.value
          );
          form.setFieldValue(field.name, selected);
        }}
      >
        {options.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </select>
    )}
  </Field>
);

const CompanyFormModal = ({
  isOpen,
  toggle,
  isEditing,
  editingCompany,
  onSaved,
  updateEndpoint, // optional override for edit submit URL
  company_login
}) => {
  // Only used to SEED Formik once when the modal opens — never mutated afterward
  const [initialFormValues, setInitialFormValues] = useState(EMPTY_COMPANY);

  useEffect(() => {
    if (!isOpen) return;

    if (isEditing && editingCompany) {
      setInitialFormValues({
        company_uid: editingCompany.company_uid,
        company_name: editingCompany.company_name,
        // country code is fixed, always +91 regardless of stored value
        company_phone_country_code: "+91",
        company_phone: editingCompany.company_phone,
        company_key_representative_email:
          editingCompany.company_key_representative_email,
        no_of_branches: Number(editingCompany.no_of_branches) || 1,
        rate_of_insurance: {
          "3000": editingCompany.rate_of_insurance?.["3000"] ?? "",
          "5000": editingCompany.rate_of_insurance?.["5000"] ?? "",
        },
        wallet_balance: editingCompany.wallet_balance ?? "",
        purpose: editingCompany.purpose || [],
        company_address: editingCompany.company_address || "",
        company_state: editingCompany.company_state || "",
        company_gst_no: editingCompany.company_gst_no || "",
      });
    } else {
      setInitialFormValues(EMPTY_COMPANY);
    }
  }, [isOpen, isEditing, editingCompany]);

  // Rebuilt whenever the modal opens for a (possibly different) company, or
  // company_login changes, so "locked"/visible fields reflect this company's
  // original data and login type.
  const validationSchema = useMemo(
    () => buildValidationSchema(isEditing, editingCompany, company_login),
    [isEditing, editingCompany, company_login]
  );

  const handleSubmit = async (values) => {
    try {
      const payload = {
        ...values,
        company_phone_country_code: "+91",
      };

      // Wallet is only seeded on create; edits go through the recharge page.
      if (isEditing) {
        delete payload.wallet_balance;
      }

      const url = isEditing
        ? updateEndpoint || `/api/updateCompany/${values.company_uid}`
        : `/api/createCompany`;

      const { data } = await axiosInstance.post(url, payload);

      if (data?.status) {
        toast.success(data?.message || data?.status, {
          position: "top-center",
        });
        if (onSaved) onSaved();
        toggle();
      } else {
        toast.error(data?.message || data?.error, { position: "top-center" });
      }
    } catch (error) {
      console.log(error);
      toast.error(error?.response?.data?.message || "An error occurred", {
        position: "top-center",
      });
    }
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="lg">
      <ModalHeader toggle={toggle} tag="h4">
        {isEditing ? "Edit Company" : "Create Company"}
      </ModalHeader>
      <ModalBody>
        <Formik
          enableReinitialize
          initialValues={initialFormValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {({ errors, touched }) => (
            <Form>
              <h5 className="mb-3">Company Information</h5>
              <Row>
                <Col md="6">
                  <div className="mb-3">
                    <Label className="form-label">Company Name *</Label>
                    <Field
                      name="company_name"
                      type="text"
                      maxLength="50"
                      className="form-control"
                    />
                    <FieldError name="company_name" />
                  </div>
                </Col>
                <Col md="6">
                  <div className="mb-3">
                    <Label className="form-label">Company Email *</Label>
                    <Field name="company_key_representative_email">
                      {({ field }) => (
                        <input
                          {...field}
                          type="email"
                          className="form-control"
                          disabled={isEditing}
                        />
                      )}
                    </Field>
                    <small className="text-muted d-block">
                      representative email
                    </small>
                    <FieldError name="company_key_representative_email" />
                  </div>
                </Col>
              </Row>

              <Row>
                <Col md="6">
                  <div className="mb-3">
                    <Label className="form-label">Company Phone *</Label>
                    <FixedCountryCodePhoneField
                      name="company_phone"
                      errors={errors.company_phone}
                      touched={touched.company_phone}
                    />
                    <small className="text-muted d-block">
                      representative phone
                    </small>
                    <FieldError name="company_phone" />
                  </div>
                </Col>
                <Col md="6">
                  <div className="mb-3">
                    <Label className="form-label">No. of Branches *</Label>
                    <Field name="no_of_branches">
                      {({ field, form }) => (
                        <input
                          {...field}
                          type="number"
                          min="1"
                          max="99"
                          step="1"
                           disabled={isEditing}
                          className={
                            "form-control" +
                            (errors.no_of_branches && touched.no_of_branches
                              ? " is-invalid"
                              : "")
                          }
                          onChange={(e) => {
                            const raw = e.target.value;
                            if (raw === "") {
                              form.setFieldValue(field.name, "");
                              return;
                            }
                            let num = parseInt(raw, 10);
                            if (Number.isNaN(num)) num = 1;
                            if (num < 1) num = 1;
                            if (num > 99) num = 99;
                            form.setFieldValue(field.name, num);
                          }}
                          onBlur={(e) => field.onBlur(e)}
                        />
                      )}
                    </Field>
                    <FieldError name="no_of_branches" />
                  </div>
                </Col>
              </Row>

              {!company_login && (
                <Row>
                  <Col md="6">
                    <div className="mb-3">
                      <Label className="form-label">
                        Rate for ₹3,000 Insurance *
                      </Label>
                      <Field name="rate_of_insurance.3000">
                        {({ field }) => (
                          <input
                            {...field}
                            type="number"
                            min="1"
                            max="1000000"
                            step="1"
                            inputMode="numeric"
                            className={
                              "form-control" +
                              (errors.rate_of_insurance?.["3000"] &&
                              touched.rate_of_insurance?.["3000"]
                                ? " is-invalid"
                                : "")
                            }
                          />
                        )}
                      </Field>
                      <FieldError name="rate_of_insurance.3000" />
                    </div>
                  </Col>
                  <Col md="6">
                    <div className="mb-3">
                      <Label className="form-label">
                        Rate for ₹5,000 Insurance *
                      </Label>
                      <Field name="rate_of_insurance.5000">
                        {({ field }) => (
                          <input
                            {...field}
                            type="number"
                            min="1"
                            max="1000000"
                            step="1"
                            inputMode="numeric"
                            className={
                              "form-control" +
                              (errors.rate_of_insurance?.["5000"] &&
                              touched.rate_of_insurance?.["5000"]
                                ? " is-invalid"
                                : "")
                            }
                          />
                        )}
                      </Field>
                      <FieldError name="rate_of_insurance.5000" />
                    </div>
                  </Col>
                </Row>
              )}

              {!company_login && (
                <Row>
                  <Col md="6">
                    <div className="mb-3">
                      <Label className="form-label">
                        Wallet Balance (₹){!isEditing && " *"}
                      </Label>
                      {isEditing ? (
                        <>
                          <input
                            type="text"
                            className="form-control"
                            value={
                              initialFormValues.wallet_balance === "" ||
                              initialFormValues.wallet_balance === undefined ||
                              initialFormValues.wallet_balance === null
                                ? "—"
                                : `₹${Number(
                                    initialFormValues.wallet_balance
                                  ).toLocaleString("en-IN")}`
                            }
                            disabled
                            readOnly
                          />
                          <small className="text-muted d-block">
                            Use the company Wallet page to recharge
                          </small>
                        </>
                      ) : (
                        <>
                          <Field name="wallet_balance">
                            {({ field }) => (
                              <input
                                {...field}
                                type="number"
                                min="0"
                                step="1"
                                inputMode="numeric"
                                className={
                                  "form-control" +
                                  (errors.wallet_balance &&
                                  touched.wallet_balance
                                    ? " is-invalid"
                                    : "")
                                }
                              />
                            )}
                          </Field>
                          <small className="text-muted d-block">
                            Initial balance; also counted toward total recharged
                          </small>
                          <FieldError name="wallet_balance" />
                        </>
                      )}
                    </div>
                  </Col>
                </Row>
              )}

              {/* Address / State / GST are only relevant for company_login companies */}
              {company_login && (
                <Row>
                  <Col md="6">
                    <div className="mb-3">
                      <Label className="form-label">
                        Company Address
                        {isEditing && initialFormValues.company_address && " *"}
                      </Label>
                      <Field
                        name="company_address"
                        as="textarea"
                        rows="2"
                        className={
                          "form-control" +
                          (errors.company_address && touched.company_address
                            ? " is-invalid"
                            : "")
                        }
                      />
                      <FieldError name="company_address" />
                    </div>
                  </Col>
                  <Col md="6">
                    <div className="mb-3">
                      <Label className="form-label">
                        Company State
                        {isEditing && initialFormValues.company_state && " *"}
                      </Label>
                      <Field
                        name="company_state"
                        type="text"
                        className={
                          "form-control" +
                          (errors.company_state && touched.company_state
                            ? " is-invalid"
                            : "")
                        }
                      />
                      <FieldError name="company_state" />
                    </div>
                  </Col>
                </Row>
              )}

              <Row>
                {company_login && (
                  <Col md="6">
                    <div className="mb-3">
                      <Label className="form-label">
                        Company GST No.
                        {isEditing && initialFormValues.company_gst_no && " *"}
                      </Label>
                      <Field
                        name="company_gst_no"
                        type="text"
                        className={
                          "form-control" +
                          (errors.company_gst_no && touched.company_gst_no
                            ? " is-invalid"
                            : "")
                        }
                      />
                      <FieldError name="company_gst_no" />
                    </div>
                  </Col>
                )}

                {/* Purpose is only relevant for non company_login companies */}
                {!company_login && (
                  <Col md="12">
                    <div className="mb-3">
                      <Label className="form-label">Purpose *</Label>
                      <PurposeMultiSelectField
                        name="purpose"
                        options={PURPOSE_OPTIONS}
                        errors={errors.purpose}
                        touched={touched.purpose}
                      />
                      <small className="text-muted d-block">
                        hold Ctrl (Cmd on Mac) to select multiple
                      </small>
                      <FieldError name="purpose" />
                    </div>
                  </Col>
                )}
              </Row>

              <Row className="mt-4">
                <Col>
                  <div className="text-end">
                    <button type="submit" className="btn btn-success">
                      {isEditing ? "Update Company" : "Save Company"}
                    </button>
                  </div>
                </Col>
              </Row>
            </Form>
          )}
        </Formik>
      </ModalBody>
    </Modal>
  );
};

export default CompanyFormModal;
